/**
 * EnemyArmyGenerator - Builds enemy armies that scale smoothly with floor depth.
 *
 * The old system used four flat bands (floors 1-2, 3-7, 8-11, 12), so every
 * floor inside a band faced an identical army and the jump between bands was
 * jarring (6 pieces on floor 7, then 14 on floor 8). This generator instead
 * spends a per-floor "threat budget" on pieces, producing a continuous
 * difficulty curve and run-to-run variety while always fitting the board.
 */

import { PieceType, Square } from '../types';

export interface EnemyPiece {
  type: PieceType;
  square: Square;
}

const PIECE_POINTS: Record<Exclude<PieceType, 'k'>, number> = {
  q: 9,
  r: 5,
  b: 3,
  n: 3,
  p: 1,
};

/**
 * Board geometry for a floor. Enemy pieces occupy the top two ranks.
 */
function boardGeometry(floor: number): { files: string[]; backRank: number; pawnRank: number } {
  if (floor <= 7) {
    return { files: ['b', 'c', 'd', 'e', 'f', 'g'], backRank: 7, pawnRank: 6 };
  } else if (floor <= 11) {
    return { files: ['b', 'c', 'd', 'e', 'f', 'g', 'h'], backRank: 8, pawnRank: 7 };
  }
  return { files: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'], backRank: 8, pawnRank: 7 };
}

/**
 * Threat budget (in material points, king excluded) for a regular combat floor.
 * Tuned so the enemy stays a step ahead of a steadily-growing player army
 * without spiking. Elite and boss floors use hand-authored encounters instead.
 */
export function threatBudget(floor: number): number {
  // Gentle on-ramp (~3 pts on floor 1) rising to ~19 by floor 11, tracking a
  // little below a steadily-recruiting player army so regular fights stay
  // competitive but winnable. Elite/boss floors override this with encounters.
  return Math.round(1.5 + floor * 1.6);
}

/**
 * Deterministic per-(seed,floor) RNG so a run's enemies are stable and the
 * save/resume flow reproduces the same armies.
 */
function makeRng(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 9301 + 49297) % 233280;
    if (h < 0) h += 233280;
    return h / 233280;
  };
}

/**
 * Generate an enemy army for a regular combat floor.
 */
export function generateEnemyArmy(floor: number, seed: string = 'default'): EnemyPiece[] {
  const { files, backRank, pawnRank } = boardGeometry(floor);
  const rng = makeRng(`${seed}|enemy|${floor}`);
  const width = files.length;

  let budget = threatBudget(floor);
  const pieces: EnemyPiece[] = [];

  // Track occupied squares on each rank
  const backUsed = new Set<number>();
  const pawnUsed = new Set<number>();

  // King always present, placed centrally on the back rank
  const kingFile = Math.floor(width / 2);
  pieces.push({ type: 'k', square: `${files[kingFile]}${backRank}` as Square });
  backUsed.add(kingFile);

  // Heavier pieces unlock with depth so early floors stay gentle
  const heavyPool: Array<Exclude<PieceType, 'k' | 'p'>> = [];
  heavyPool.push('n', 'b');
  if (floor >= 3) heavyPool.push('r');
  if (floor >= 5) heavyPool.push('n', 'b', 'r');
  if (floor >= 8) heavyPool.push('q', 'r');
  if (floor >= 10) heavyPool.push('q');

  // Spend most of the budget on back-rank officers, keeping a reserve for pawns.
  // Reserve at least ~30% of budget (min 2) for pawns so armies always have a
  // front line.
  const pawnReserve = Math.max(2, Math.floor(budget * 0.3));
  let officerBudget = budget - pawnReserve;

  const backSlots = width - 1; // minus king
  let placedOfficers = 0;
  let safety = 0;
  while (officerBudget > 0 && placedOfficers < backSlots && safety++ < 100) {
    const candidates = heavyPool.filter(t => PIECE_POINTS[t] <= officerBudget);
    if (candidates.length === 0) break;
    const type = candidates[Math.floor(rng() * candidates.length)];

    // Find a free back-rank file (prefer spreading outward from center)
    const freeFiles = files.map((_, i) => i).filter(i => !backUsed.has(i));
    if (freeFiles.length === 0) break;
    const fileIdx = freeFiles[Math.floor(rng() * freeFiles.length)];

    pieces.push({ type, square: `${files[fileIdx]}${backRank}` as Square });
    backUsed.add(fileIdx);
    officerBudget -= PIECE_POINTS[type];
    placedOfficers++;
  }

  // Remaining budget (the pawn reserve plus any officer leftovers) buys pawns.
  budget = pawnReserve + Math.max(0, officerBudget);
  let safety2 = 0;
  while (budget > 0 && pawnUsed.size < width && safety2++ < 100) {
    const freeFiles = files.map((_, i) => i).filter(i => !pawnUsed.has(i));
    if (freeFiles.length === 0) break;
    const fileIdx = freeFiles[Math.floor(rng() * freeFiles.length)];
    pieces.push({ type: 'p', square: `${files[fileIdx]}${pawnRank}` as Square });
    pawnUsed.add(fileIdx);
    budget -= 1;
  }

  return pieces;
}

/**
 * Total material points (king excluded) of an army — used by balance tests.
 */
export function armyValue(pieces: EnemyPiece[]): number {
  return pieces.reduce((sum, p) => sum + (p.type === 'k' ? 0 : PIECE_POINTS[p.type as Exclude<PieceType, 'k'>]), 0);
}
